/**
 * dashboards/api/dashboardsApi.js
 *
 * Dashboards CRUD + widget mutations + batched run. After wedge 2 of the
 * analytics backend migration the CRUD half hits /api/v1/dashboards/* —
 * `run` remains a client-side loop (per-widget aggregation via the same
 * reportEngine path useWidgetData uses) because execution still lives in
 * the browser; that's wedge 5.
 *
 * Endpoints (see backend/modules/dashboards/api/dashboards_routes.py):
 *   GET    /dashboards                            list (+ filters)
 *   GET    /dashboards/:id                        read
 *   POST   /dashboards                            upsert by client-generated id
 *   DELETE /dashboards/:id                        delete (cascades favorites/recents)
 *   POST   /dashboards/:id/duplicate              server-side copy with fresh widget ids
 *   POST   /dashboards/:id/favorite               per-user favorite toggle
 *   POST   /dashboards/:id/mark-viewed            stamp lastViewed + bump recents
 *   PATCH  /dashboards/:id/folder                 body { folder }
 *   PATCH  /dashboards/:id/layout                 body { layouts, version? }
 */
import { analyticsClient, unwrap } from '../../analytics-shared/api/client';

const uid = (p = 'd') => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

/**
 * Build a fresh widget. The richer `config` covers per-widget chart axes
 * (xField / yField / aggregate / axis labels / display toggles) so each
 * widget can be reconfigured on the dashboard without touching the
 * underlying report.
 *
 * Auto-derivation from the report (groupBy/summaries/embedded chart def)
 * is the caller's job — see DashboardBuilderPage's handleAddReport which
 * runs deriveWidgetConfig() before instantiation.
 */
export const createWidget = (reportId, overrides = {}) => {
  const id = uid('wid');
  const { config: configOverride = {}, ...rest } = overrides;
  return {
    id,
    reportId,
    title: 'Untitled Widget',
    subtitle: '',
    chartType: 'bar',
    chartIndex: 0,
    layout: { i: id, x: 0, y: 0, w: 6, h: 4, minW: 2, minH: 2 },
    config: {
      // Display toggles
      showTitle: true,
      drillDownEnabled: true,
      showLegend: true,
      showGrid: true,
      showTooltip: true,

      // Chart axes — populated by the caller (deriveWidgetConfig) or by
      // the user via WidgetSettingsPanel. `aggregate` defaults to COUNT so
      // a widget without a yField still renders something useful.
      xField: null,
      yField: null,
      aggregate: 'COUNT',
      xAxisLabel: '',
      yAxisLabel: '',

      // Table-widget column override. Empty array = inherit from the
      // underlying report's columns.
      tableColumns: [],

      ...configOverride,
    },
    ...rest,
  };
};

export const createDashboard = (overrides = {}) => ({
  id: uid('dash'),
  name: 'Untitled Dashboard',
  description: '',
  folder: 'Unfiled',
  visibility: 'Private',
  tags: [],
  theme: 'default',
  favorite: false,
  createdBy: 'You',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  lastViewed: null,
  widgets: [],
  global_filters: [],
  layout_version: 1,
  ...overrides,
});

function toParams({ q, folder, visibility, favorite } = {}) {
  const params = {};
  if (q) params.q = q;
  if (folder) params.folder = folder;
  if (visibility) params.visibility = visibility;
  if (favorite) params.favorite = true;
  return params;
}

export const dashboardsApi = {
  list: async (filters) => analyticsClient
    .get('/dashboards', { params: toParams(filters) })
    .then(unwrap),

  get: async (id) => analyticsClient
    .get(`/dashboards/${encodeURIComponent(id)}`)
    .then(unwrap)
    .catch((err) => {
      if (err?.status === 404) return null;
      throw err;
    }),

  save: async (dashboard) => analyticsClient
    .post('/dashboards', dashboard)
    .then(unwrap),

  remove: async (id) => analyticsClient
    .delete(`/dashboards/${encodeURIComponent(id)}`)
    .then(unwrap),

  duplicate: async (id) => analyticsClient
    .post(`/dashboards/${encodeURIComponent(id)}/duplicate`)
    .then(unwrap),

  toggleFavorite: async (id) => analyticsClient
    .post(`/dashboards/${encodeURIComponent(id)}/favorite`)
    .then(unwrap),

  markViewed: async (id) => analyticsClient
    .post(`/dashboards/${encodeURIComponent(id)}/mark-viewed`)
    .then(unwrap),

  moveToFolder: async (id, folder) => analyticsClient
    .patch(`/dashboards/${encodeURIComponent(id)}/folder`, { folder })
    .then(unwrap),

  // Server applies the same layout-merge semantics the old localStorage
  // path did (walk existing widgets, merge by `i === widget.id`). Returns
  // the refreshed dashboard so the caller can update its in-memory copy
  // without a separate GET.
  saveLayout: async (id, widgetLayouts, version) => analyticsClient
    .patch(`/dashboards/${encodeURIComponent(id)}/layout`, {
      layouts: widgetLayouts,
      version,
    })
    .then(unwrap),

  /**
   * Run every widget on the dashboard in a single backend round-trip.
   *
   * After wedge 5 this is just a thin pass-through: the server-side
   * analytics_engine.runner.run_dashboard handles record fetch + joins
   * + filtering + aggregation + chart-data construction, returning the
   * same `{widget_id: payload}` shape useWidgetData used to build
   * client-side. No more N-roundtrips-per-dashboard or duplicated
   * aggregation logic.
   */
  run: async (dashboardId /* , globalFilters */) => analyticsClient
    .post(`/dashboards/${encodeURIComponent(dashboardId)}/run`)
    .then(unwrap)
    .catch((err) => {
      if (err?.status === 404) return {};
      throw err;
    }),
};

export { uid };
